// App-owned car badges (#281). Typographic marks in the series-badge SHAPE (dark
// rounded square + 2px semantic frame), colored by 3 discipline FAMILIES (not 8
// decorative hues — brand decision 0005): NASCAR=red, short-track=yellow,
// road=purple. A self-contained inline SVG so it sizes from one source and reads
// on any background (incl. the selected --live switcher segment). Always paired
// with the human label (carLabel) — the badge is recognition, the text is the name.

type Family = "nascar" | "short" | "road";

const CAR_BADGES: Record<string, { abbr: string; family: Family }> = {
  "nascar-cup-next-gen": { abbr: "CUP", family: "nascar" },
  "nascar-oreilly": { abbr: "XFIN", family: "nascar" }, // drivers say "Xfinity", not the sponsor
  "nascar-trucks": { abbr: "TRK", family: "nascar" },
  "late-model": { abbr: "LM", family: "short" },
  "super-late-model": { abbr: "SLM", family: "short" },
  "modified": { abbr: "MOD", family: "short" },
  "gt3": { abbr: "GT3", family: "road" },
  "gt4": { abbr: "GT4", family: "road" },
};

const FAMILY_COLOR: Record<Family, string> = {
  nascar: "var(--flag-red)",
  short: "var(--flag-yellow)",
  road: "var(--flag-purple)",
};

function metaFor(slug: string): { abbr: string; family: Family } {
  return (
    CAR_BADGES[slug] || {
      abbr: (slug.replace(/[^a-z0-9]/gi, "").slice(0, 3) || "?").toUpperCase(),
      family: "road",
    }
  );
}

export function CarBadge({ slug, size = 18 }: { slug: string; size?: number }) {
  const { abbr, family } = metaFor(slug);
  const color = FAMILY_COLOR[family];
  // Shrink the word as it gets longer so 4 chars (XFIN) still fit the 64 box.
  const fontSize = abbr.length >= 4 ? 19 : abbr.length === 3 ? 24 : 30;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={`${abbr} car badge`}
      style={{ display: "block", flexShrink: 0 }}
    >
      <rect width="64" height="64" rx="12" fill="var(--ink-0)" />
      <rect x="2.5" y="2.5" width="59" height="59" rx="10" fill="none" stroke={color} strokeWidth="2.5" />
      <text
        x="32"
        y="32"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-body)"
        fontWeight={800}
        fontSize={fontSize}
        letterSpacing="0.4"
        fill="var(--text-strong)"
      >
        {abbr}
      </text>
    </svg>
  );
}
